/*
 * BS.Analytics.Client: reports the page's errors to the site itself (BS.Analytics.Server MapBsClientErrors, default
 * /_bs/client-errors). The TypeScript source of the static web asset _content/BS.Analytics.Client/js/errors.js (wwwroot/js/errors.js,
 * a classic script compiled on build to ES2019 with a source map). Load it as the first script of the page; another endpoint goes
 * into data-endpoint:
 *
 *   <script src="/_content/BS.Analytics.Client/js/errors.js"></script>
 *   <script src="/_content/BS.Analytics.Client/js/errors.js" data-endpoint="/api/client-errors"></script>
 *
 * It reports uncaught errors, unhandled promise rejections, console errors (Blazor logs its crashes there) and the page's own
 * scripts and stylesheets that fail to load: at most 10 per page, each once, sent two seconds after the first one of a batch and
 * when the page is hidden or left (sendBeacon, else fetch with keepalive). No cookies, no storage, no query strings.
 *
 * Not ours to fix, so never reported: errors from browser extensions, cross-origin scripts without details ("Script error."), and
 * load failures of scripts and stylesheets from other origins. Google Tag Manager, Google Analytics, Microsoft Clarity, Freshchat or
 * a font service that an ad or tracking blocker stopped (or that is down) is not the site's error and would only fill the alerts.
 *
 * One job per part (SOLID): PageAddresses knows whose files are the page's own and shortens addresses; NoiseFilter leaves out what
 * is not ours to fix; BeaconTransport sends a batch (ITransport); ErrorQueue limits, deduplicates and batches the reports; listen
 * wires the browser's events to the queue. All of it lives in one function scope: nothing is left in the global scope.
 */
(() => {
  /** The endpoint without data-endpoint. */
  const DEFAULT_ENDPOINT = "/_bs/client-errors";

  /** At most this many reports per page. */
  const MAX_REPORTS = 10;

  /** A batch goes out this long after its first report (or earlier, when the page is hidden or left). */
  const BATCH_DELAY_MS = 2000;

  /** Browser extensions and cross-origin scripts without details are not ours to fix. */
  const NOISE = /-extension:\/\/|webkit-masked-url:|^Script error\.?$/i;

  /** An error as BS.Analytics.Server's BrowserError reads it: the wire contract, names and meaning stay. */
  interface BrowserError {
    kind: string;
    message: string;
    source: string | null;
    line: number | null;
    column: number | null;
    stack: string | null;
    page: string;
    at: number;
  }

  /** A script or stylesheet element whose file failed to load: the target of the error event on its way down. */
  interface ResourceElement {
    readonly tagName: string;
    readonly src?: unknown;
    readonly href?: unknown;
  }

  /** Sends a batch of reports to the site. */
  interface ITransport {
    send(batch: BrowserError[]): void;
  }

  /** The text of a value, at most length characters; null for nothing. */
  function clip(value: unknown, length: number): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const text = String(value);
    return text.length > length ? text.slice(0, length) : text;
  }

  /** The stack of a thrown value, when it has one. */
  function stackOf(value: unknown): unknown {
    return value !== null && typeof value === "object" ? (value as { stack?: unknown }).stack : undefined;
  }

  /** The message of a thrown value, when it has one. */
  function messageOf(value: unknown): unknown {
    return value !== null && typeof value === "object" ? (value as { message?: unknown }).message : undefined;
  }

  /** One argument of console.error as text: an error's stack, else its message, else the value itself. */
  function describe(value: unknown): string {
    const stack = stackOf(value);
    if (stack) {
      return String(stack);
    }
    const message = messageOf(value);
    return message ? String(message) : String(value);
  }

  /** The page's own origin decides whose files are ours; addresses lose their query. */
  class PageAddresses {
    public constructor(private readonly page: Location) {}

    /** The address without its query: the path for the page's own files, origin and path for another origin's. */
    public file(url: string | null): string | null {
      if (!url) {
        return null;
      }
      try {
        const address = new URL(url, this.page.href);
        return address.origin === this.page.origin ? address.pathname : address.origin + address.pathname;
      } catch {
        return clip(url, 300);
      }
    }

    /** True for an address of another origin; one that cannot be read counts as the page's own. */
    public foreign(url: string): boolean {
      try {
        return new URL(url, this.page.href).origin !== this.page.origin;
      } catch {
        return false;
      }
    }
  }

  /** Leaves out what is not ours to fix: anything that names a browser extension, and "Script error.". */
  class NoiseFilter {
    public ignores(message: string, source: string | null, stack: string | null): boolean {
      return NOISE.test(message) || NOISE.test(source || "") || NOISE.test(stack || "");
    }
  }

  /** sendBeacon, which outlives the page; fetch with keepalive where the beacon is missing or refused. */
  class BeaconTransport implements ITransport {
    public constructor(private readonly endpoint: string) {}

    public send(batch: BrowserError[]): void {
      const body = JSON.stringify(batch);
      try {
        if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(this.endpoint, new Blob([body], { type: "application/json" }))) {
          return;
        }
      } catch {
        // fall back to fetch
      }
      try {
        // A report that cannot be sent is dropped; it never becomes an unhandled rejection to report.
        fetch(this.endpoint, { method: "POST", body: body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(
          () => undefined,
        );
      } catch {
        // nothing else to try
      }
    }
  }

  /** The page's reports: at most MAX_REPORTS, each once, sent in batches. */
  class ErrorQueue {
    private readonly queue: BrowserError[] = [];
    private readonly seen = new Set<string>();
    private sent = 0;
    private timer: number | null = null;

    public constructor(
      private readonly transport: ITransport,
      private readonly addresses: PageAddresses,
      private readonly noise: NoiseFilter,
    ) {}

    public report(kind: string, message: unknown, source: string | null, line: number | null, column: number | null, stack: unknown): void {
      if (this.sent + this.queue.length >= MAX_REPORTS) {
        return;
      }
      const text = clip(message, 500);
      const trace = stack === null || stack === undefined ? null : String(stack);
      if (!text || this.noise.ignores(text, source, trace)) {
        return;
      }
      const key = `${kind}|${text}|${source}|${line}`;
      if (this.seen.has(key)) {
        return;
      }
      this.seen.add(key);
      this.queue.push({
        kind: kind,
        message: text,
        source: clip(this.addresses.file(source), 300),
        line: line || null,
        column: column || null,
        stack: clip(trace, 4000),
        page: window.location.pathname,
        at: Math.round(performance.now()),
      });
      if (this.timer === null) {
        this.timer = window.setTimeout(() => this.flush(), BATCH_DELAY_MS);
      }
    }

    public flush(): void {
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.queue.length === 0) {
        return;
      }
      const batch = this.queue.splice(0, this.queue.length);
      this.sent += batch.length;
      this.transport.send(batch);
    }
  }

  /** The element whose file failed to load, when the error event is about a script or a stylesheet. */
  function failedResource(target: EventTarget | null): ResourceElement | null {
    if (target === null || target === window) {
      return null;
    }
    const element = target as Partial<ResourceElement>;
    const tag = typeof element.tagName === "string" ? element.tagName.toUpperCase() : "";
    return tag === "SCRIPT" || tag === "LINK" ? (element as ResourceElement) : null;
  }

  /** The address of a script (src) or stylesheet (href). */
  function addressOf(resource: ResourceElement): string {
    if (typeof resource.src === "string" && resource.src) {
      return resource.src;
    }
    return typeof resource.href === "string" ? resource.href : "";
  }

  /** Wires the browser's error events, console.error and the page leaving to the queue. */
  function listen(queue: ErrorQueue, addresses: PageAddresses): void {
    // Caught on its way down, so the load failures of scripts and stylesheets (which do not bubble) arrive too.
    window.addEventListener(
      "error",
      (event: Event) => {
        const resource = failedResource(event.target);
        if (resource !== null) {
          const address = addressOf(resource);
          if (!addresses.foreign(address)) {
            queue.report("resource", "Failed to load " + resource.tagName.toLowerCase(), address || null, null, null, null);
          }
          return;
        }
        const error = event as ErrorEvent;
        queue.report("error", error.message, error.filename || null, error.lineno, error.colno, stackOf(error.error));
      },
      true,
    );

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      const message = messageOf(reason);
      queue.report("rejection", message ? message : reason, null, null, null, stackOf(reason));
    });

    const original = console.error;
    console.error = (...args: unknown[]): void => {
      try {
        const text = args.map(describe).join(" ");
        queue.report("console", text.split("\n")[0], null, null, null, text);
      } catch {
        // reporting never breaks logging
      }
      original.apply(console, args);
    };

    window.addEventListener("pagehide", () => queue.flush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        queue.flush();
      }
    });
  }

  const script = document.currentScript;
  const endpoint = (script && script.getAttribute("data-endpoint")) || DEFAULT_ENDPOINT;
  const addresses = new PageAddresses(window.location);
  listen(new ErrorQueue(new BeaconTransport(endpoint), addresses, new NoiseFilter()), addresses);
})();
